import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { mergeClasses, type ClassValue } from '../../utils/merge-classes';
import { OptionSliderOptions } from './option-slider.types';
import {
  optionSliderDotVariants,
  optionSliderVariants,
  type OptionSliderVariant,
} from './option-slider.variants';

const MAX_OPTIONS = 4;
const HANDLE_COVER_PERCENT = 2;

interface OptionSliderDot {
  className: string;
  left: string;
}

@Component({
  selector: 'app-option-slider',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './option-slider.component.html',
})
export class OptionSliderComponent implements OnDestroy {
  @Input() title = '';
  @Input() value: string | null = null;
  @Input() variant: OptionSliderVariant = 'stops';
  @Input() class: ClassValue = '';
  @Output() valueChange = new EventEmitter<string>();

  displayedOptions: OptionSliderOptions[] = [];
  isDragging = false;

  private dragRatio: number | null = null;
  private trackEl: HTMLElement | null = null;
  private activePointerId: number | null = null;

  private readonly handlePointerMove = (event: PointerEvent) => this.onPointerMove(event);
  private readonly handlePointerUp = (event: PointerEvent) => this.onPointerUp(event);

  @Input()
  set options(value: OptionSliderOptions[]) {
    this.displayedOptions = (value ?? []).slice(0, MAX_OPTIONS);
  }

  get selectedIndex(): number {
    return Math.max(0, this.displayedOptions.findIndex(option => option.value === this.value));
  }

  get rangeMax(): number {
    return Math.max(0, this.displayedOptions.length - 1);
  }

  get fillPercent(): number {
    if (this.dragRatio !== null) {
      return this.dragRatio * 100;
    }
    if (this.displayedOptions.length <= 1) {
      return 0;
    }
    return (this.selectedIndex / (this.displayedOptions.length - 1)) * 100;
  }

  get isAtStart(): boolean {
    return this.fillPercent <= 0;
  }

  get isAtEnd(): boolean {
    return this.fillPercent >= 100;
  }

  get showCutout(): boolean {
    return !this.isDragging;
  }

  get classes(): string {
    return mergeClasses(
      optionSliderVariants({ variant: this.variant }),
      this.class,
    );
  }

  get dots(): OptionSliderDot[] {
    const count = this.displayedOptions.length;
    if (count <= 1) {
      return [];
    }

    const indices = this.variant === 'stops'
      ? Array.from({ length: count }, (_, i) => i)
      : [count - 1];

    return indices.flatMap(index => {
      const percent = (index / (count - 1)) * 100;
      if (Math.abs(percent - this.fillPercent) < HANDLE_COVER_PERCENT) {
        return [];
      }
      return [{
        className: optionSliderDotVariants({
          tone: percent < this.fillPercent ? 'active' : 'inactive',
        }),
        left: this.dotLeft(percent),
      }];
    });
  }

  select(value: string): void {
    if (value === this.value) {
      return;
    }
    this.value = value;
    this.valueChange.emit(value);
  }

  onRangeInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.selectByIndex(Number(target.value));
  }

  onPointerDown(event: PointerEvent): void {
    if (event.button !== 0) {
      return;
    }
    event.preventDefault();
    this.trackEl = event.currentTarget as HTMLElement;
    this.activePointerId = event.pointerId;
    this.isDragging = true;
    this.capturePointer(this.trackEl, event);
    this.unbindDragListeners();
    this.bindDragListeners();
    this.applyPointer(event);
  }

  private onPointerMove(event: PointerEvent): void {
    if (!this.isDragging) {
      return;
    }
    this.applyPointer(event);
  }

  private onPointerUp(event: PointerEvent): void {
    if (!this.isDragging) {
      return;
    }
    this.applyPointer(event);
    this.endDrag();
  }

  ngOnDestroy(): void {
    this.unbindDragListeners();
  }

  onKeydown(event: KeyboardEvent): void {
    if (this.displayedOptions.length === 0 || event.target instanceof HTMLInputElement) {
      return;
    }
    if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.selectByIndex(Math.min(this.displayedOptions.length - 1, this.selectedIndex + 1));
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
      event.preventDefault();
      this.selectByIndex(Math.max(0, this.selectedIndex - 1));
    } else if (event.key === 'Home') {
      event.preventDefault();
      this.selectByIndex(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      this.selectByIndex(this.displayedOptions.length - 1);
    }
  }

  private applyPointer(event: PointerEvent): void {
    const ratio = this.ratioFromClientX(event.clientX);
    if (ratio === null) {
      return;
    }
    this.dragRatio = ratio;
    this.selectByRatio(ratio);
  }

  private endDrag(): void {
    if (this.dragRatio !== null) {
      this.selectByRatio(this.dragRatio);
    }
    this.releasePointer();
    this.unbindDragListeners();
    this.isDragging = false;
    this.dragRatio = null;
    this.trackEl = null;
    this.activePointerId = null;
  }

  private ratioFromClientX(clientX: number): number | null {
    const track = this.trackEl;
    if (!track) {
      return null;
    }
    const rect = track.getBoundingClientRect();
    if (rect.width <= 0) {
      return null;
    }
    return Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
  }

  private bindDragListeners(): void {
    document.addEventListener('pointermove', this.handlePointerMove);
    document.addEventListener('pointerup', this.handlePointerUp);
    document.addEventListener('pointercancel', this.handlePointerUp);
  }

  private unbindDragListeners(): void {
    document.removeEventListener('pointermove', this.handlePointerMove);
    document.removeEventListener('pointerup', this.handlePointerUp);
    document.removeEventListener('pointercancel', this.handlePointerUp);
  }

  private capturePointer(track: HTMLElement, event: PointerEvent): void {
    if (event.buttons === 0 || typeof track.setPointerCapture !== 'function') {
      return;
    }
    track.setPointerCapture(event.pointerId);
  }

  private releasePointer(): void {
    const track = this.trackEl;
    const pointerId = this.activePointerId;
    if (
      !track ||
      pointerId === null ||
      typeof track.hasPointerCapture !== 'function' ||
      !track.hasPointerCapture(pointerId)
    ) {
      return;
    }
    track.releasePointerCapture(pointerId);
  }

  private selectByRatio(ratio: number): void {
    if (this.displayedOptions.length === 0) {
      return;
    }
    const index = Math.round(ratio * (this.displayedOptions.length - 1));
    this.selectByIndex(index);
  }

  private selectByIndex(index: number): void {
    const option = this.displayedOptions[index];
    if (option) {
      this.select(option.value);
    }
  }

  private dotLeft(percent: number): string {
    if (percent <= 0) {
      return '4px';
    }
    if (percent >= 100) {
      return 'calc(100% - 8px)';
    }
    return `calc(${percent}% - 2px)`;
  }
}
